import { useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, clearCsrfToken } from "@/lib/queryClient";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { LogOut, PenLine, BarChart3, Layers, TrendingUp, MessageCircle, ClipboardList, CalendarDays, Home, Settings, HelpCircle, Shield } from "lucide-react";

const navItems = [
  { path: "/", label: "HOME", sublabel: "your daily intention", icon: Home },
  { path: "/chat", label: "TALK TO FRACTAL", sublabel: "what's on your mind", icon: MessageCircle },
  { path: "/entries", label: "ENTRIES", sublabel: "what's been captured", icon: ClipboardList },
  { path: "/recaps", label: "DAILY RECAPS", sublabel: "end of day reflections", icon: CalendarDays },
  { path: "/patterns", label: "PATTERNS", sublabel: "what Fractal sees in you", icon: Layers },
  { path: "/log", label: "MOMENT", sublabel: "capture right now", icon: PenLine },
  { path: "/trends", label: "TRENDS", sublabel: "how you've been", icon: TrendingUp },
  { path: "/settings", label: "SETTINGS", sublabel: "name, photo, preferences", icon: Settings },
];

export function AppSidebar() {
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ["/api/status"],
  });

  const { data: user } = useQuery<any>({
    queryKey: ["/api/auth/me"],
  });

  const logoutMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/logout"),
    onSuccess: () => {
      clearCsrfToken();
      queryClient.clear();
      setLocation("/auth");
    },
  });

  const logCount = (status as any)?.count ?? 0;
  const statusMessage = (status as any)?.message ?? "Patterns need time.";
  const displayName = user?.displayName || user?.email?.split("@")[0] || "you";

  return (
    <Sidebar>
      <SidebarHeader className="px-5 pt-6 pb-5" style={{ borderBottom: "1px solid var(--border-medium)" }}>
        <div>
          <div
            className="text-lg tracking-[0.1em]"
            style={{ color: "var(--fg-raw)", fontFamily: "var(--font-display)" }}
          >
            FRACTAL
          </div>
          <div
            className="text-xs mt-1 text-mirror-text-muted"
            style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)",
              letterSpacing: "var(--type-label-tracking)", fontSize: "var(--type-label-size)", textTransform: "uppercase" }}
          >
            pattern intelligence
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="py-3">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map(item => {
                const isActive = location === item.path
                  || (item.path === "/entries" && location.startsWith("/entries/"));
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton asChild>
                      <Link
                        href={item.path}
                        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                        className={cn(
                          "flex items-center gap-3 px-5 py-3 w-full transition-all duration-200",
                          "rounded-lg mx-2"
                        )}
                        style={{
                          background: isActive ? "var(--accent-bg-strong)" : "transparent",
                          marginBottom: "2px",
                        }}
                      >
                        <Icon
                          size={15}
                          style={{
                            color: isActive ? "var(--accent-color)" : "var(--text-muted)",
                            transition: "color 0.2s",
                          }}
                        />
                        <div className="flex flex-col gap-0.5 text-xs">
                          <span
                            style={{
                              color: isActive ? "var(--fg-raw)" : "var(--text-secondary)",
                              transition: "color 0.2s",
                              fontFamily: "var(--font-mono)",
                              fontSize: "var(--type-label-size)",
                              letterSpacing: "var(--type-label-tracking)",
                              textTransform: "uppercase",
                            }}
                          >
                            {item.label}
                          </span>
                          <span
                            className="text-xs"
                            style={{
                              color: isActive ? "var(--text-secondary)" : "var(--text-muted)",
                              fontSize: "0.9375rem",
                              fontWeight: 400,
                              fontFamily: "var(--font-reflective)",
                              transition: "color 0.2s",
                            }}
                          >
                            {item.sublabel}
                          </span>
                        </div>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              {user?.isAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link
                      href="/admin"
                      data-testid="nav-admin"
                      className={cn(
                        "flex items-center gap-3 px-5 py-3 w-full transition-all duration-200",
                        "rounded-lg mx-2"
                      )}
                      style={{
                        background: location === "/admin" ? "var(--accent-bg-strong)" : "transparent",
                        marginBottom: "2px",
                      }}
                    >
                      <Shield
                        size={15}
                        style={{
                          color: location === "/admin" ? "var(--accent-color)" : "var(--text-muted)",
                          transition: "color 0.2s",
                        }}
                      />
                      <div className="flex flex-col gap-0.5 text-xs">
                        <span
                          style={{
                            color: location === "/admin" ? "var(--fg-raw)" : "var(--text-secondary)",
                            transition: "color 0.2s",
                            fontFamily: "var(--font-mono)",
                            fontSize: "var(--type-label-size)",
                            letterSpacing: "var(--type-label-tracking)",
                            textTransform: "uppercase",
                          }}
                        >
                          ADMIN
                        </span>
                        <span
                          className="text-xs"
                          style={{
                            color: location === "/admin" ? "var(--text-secondary)" : "var(--text-muted)",
                            fontSize: "0.9375rem",
                            fontWeight: 400,
                            fontFamily: "var(--font-reflective)",
                            transition: "color 0.2s",
                          }}
                        >
                          dashboard
                        </span>
                      </div>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="px-5 pb-5 pt-3 space-y-3" style={{ borderTop: "1px solid var(--border-medium)" }}>
        <div>
          <div className="flex justify-between items-center text-xs text-mirror-text-muted">
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--type-label-size)",
                letterSpacing: "var(--type-label-tracking)",
                textTransform: "uppercase",
                color: "var(--text-muted)",
              }}
            >
              ENTRIES
            </span>
            <span
              className="text-xs font-semibold text-mirror-text-secondary"
              data-testid="text-log-count"
              style={{
                fontFamily: "var(--font-display)",
                color: "var(--text-secondary)",
              }}
            >
              {logCount}
            </span>
          </div>
          <div
            className="h-1 relative overflow-hidden"
            style={{ width: "100%", background: "var(--surface-3)", borderRadius: "2px" }}
          >
            <div
              className="absolute inset-y-0 left-0"
              style={{
                width: `${Math.min(100, (logCount / 30) * 100)}%`,
                backgroundColor: "var(--accent-color)",
                opacity: 0.5,
                transition: "width 0.8s ease",
                borderRadius: "2px",
              }}
            />
          </div>
          <div
            className="text-xs leading-relaxed"
            style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)" }}
          >
            {statusMessage}
          </div>

          <button
            onClick={() => window.dispatchEvent(new CustomEvent("mirror-retake-tutorial"))}
            className="touch-target flex items-center gap-2.5 w-full py-2 px-1 rounded-md transition-colors"
            style={{ color: "var(--text-muted)" }}
            data-testid="button-retake-tutorial"
          >
            <HelpCircle size={13} />
            <span
              className="text-xs"
              style={{
                color: "var(--text-muted)",
                fontFamily: "var(--font-display)",
                fontSize: "0.75rem",
                fontWeight: 500,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Retake tutorial
            </span>
          </button>

          <div
            className="flex items-center justify-between pt-3"
            style={{ borderTop: "1px solid var(--border-light)" }}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt=""
                  className="w-6 h-6 rounded-full object-cover shrink-0"
                  style={{ border: "1px solid var(--accent-border)" }}
                  data-testid="img-sidebar-avatar"
                />
              ) : null}
              <span
                className="text-xs truncate"
                style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)" }}
                data-testid="text-user-name"
              >
                {displayName}
              </span>
            </div>
            <button
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
              data-testid="button-logout"
              className="touch-target opacity-60 hover:opacity-100 transition-opacity disabled:opacity-60 p-1 rounded shrink-0"
            >
              <LogOut size={13} style={{ color: "var(--fg-raw)" }} />
            </button>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
